<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-card ref="rootCard" class="fill-height">
			<v-card id="playlist-toolbar-container" ref="toolbarContainer" rounded="b-0">
				<Toolbar :items="toolbarItems" :panel-params="params" />
				<Toolbar v-if="toolbarEpisode" :items="headerItems" />
			</v-card>
			<v-card id="playlist-container" class="d-flex h-100" rounded="t-0">
				<perfect-scrollbar
					v-if="selectedStation"
					id="playlist-clock"
					ref="playlistClockScrollbar"
					min-scrollbar-length="4"
					max-scrollbar-length="4"
				>
					<Clock
						v-if="!externalPlaylistFound"
						:selected-station="selectedStation"
						:selected-date-time="selectedDateTime"
						:station-current-hour="stationCurrentHour"
						:from="24"
						:to="24"
						:scrollbar="true"
						:tab-height="clockTabHeight"
						:sequencer-show-episode-i-d="sequencerShowEpisodeID"
						@select-date-time="hourClicked"
					/>
				</perfect-scrollbar>
				<perfect-scrollbar
					v-if="selectedStation && assetTypes"
					id="playlist-events-container"
					ref="scrollbar"
					:options="playlistScrollbarOptions"
					:watch-options="true"
					@ps-scroll-y="debouncedOnScrollY"
				>
					<v-expansion-panels multiple variant="accordion" class="flex-column">
						<div v-for="playlist in playlistsList" :key="playlist.PlaylistID" class="w-100">
							<draggable
								:list="
									playlist.TopOfTheHour
										? [playlist.TopOfTheHour].concat(playlist.Events)
										: playlist.Events
								"
								:group="{
									name: 'playlist',
									pull: 'clone',
									put: ['log', 'library-assets', 'playlist', 'hotkeys']
								}"
								:clone="handleDraggedClone"
								:move="onMove"
								:fallback-on-body="true"
								filter=".not-draggable"
								:force-fallback="true"
								:fallback-tolerance="3"
								drag-class="dragged-playlist"
								data-drop-container="playlist"
								class="drop-container"
								@end="onDropItem"
							>
								<PlaylistTopOfTheHour
									v-if="playlist.TopOfTheHour"
									:toh="playlist.TopOfTheHour"
									:selected-station="selectedStation"
								/>
								<PlaylistEvent
									v-for="event in playlist.Events"
									:id="event.EventID"
									:key="event.EventID"
									:selected-station="selectedStation"
									:event="event"
									:data-station-id="event.StationID"
									:data-playlist-id="event.PlaylistID"
									:data-child-playlist-id="event.ChildPlaylistID"
									:class="{'not-draggable': !isDraggableEvent(event)}"
									:dragged-event="draggedEvent"
									:drop-target-event="loadDropTargetEvent(event.EventID)"
									:parent-component="this"
									:copied-event="copiedEvent"
									type="playlistEvent"
									@copy-event="copyEvent(event)"
									@paste-event="pasteEvent"
								/>
								<PlaylistEmptyContainer
									v-if="!playlist.Events.length"
									:id="`empty-playlist-${playlist.PlaylistID}`"
									class="not-draggable empty-playlist-event"
									:playlist="playlist"
									:data-station-id="playlist.StationID"
									:data-playlist-id="playlist.PlaylistID"
									:drop-target-event="loadDropTargetEvent(`empty-playlist-${playlist.PlaylistID}`)"
								/>
							</draggable>
						</div>
					</v-expansion-panels>
				</perfect-scrollbar>
				<NoStationSelectedContainer v-if="!selectedStation" />
				<Loader v-else :value="!playlistsList.length || loading" />
			</v-card>
			<EditExternalPlaylistEventDialog
				v-if="editExternalPlaylistEventDialog.open"
				:dialog="editExternalPlaylistEventDialog"
				:options="editExternalPlaylistEventDialog.options"
				@close="editExternalPlaylistEventDialog.open = false"
			/>
			<ShowAudioFeedsDialog
				v-if="showAudioFeedsDialog.open"
				:dialog="showAudioFeedsDialog"
				:options="showAudioFeedsDialog.options"
				@close="showAudioFeedsDialog.open = false"
			/>
			<CopyPlaylistDialog
				:is-open="isCopyPlaylistDialogOpen"
				:source-station="selectedStation"
				@close="isCopyPlaylistDialogOpen = false"
			/>
			<v-dialog v-model="overrideSegueTransitionsModal.open" width="350" persistent>
				<GeneralConfirmation
					message="Do you want to Override Segue Transitions?"
					:button-options="overrideSegueTransitionsButtonOptions"
					@agree="() => overrideSegueTransitionsModal.resolve(true)"
					@cancel="() => overrideSegueTransitionsModal.resolve(false)"
				/>
			</v-dialog>
		</v-card>
	</v-defaults-provider>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import {VueDraggableNext} from "vue-draggable-next";
import emitter from "@/emitter";
import moment from "moment-timezone";
import {uuid} from "vue-uuid";

/* Modules */
import {VBtn, VList, VListItem, VMenu, VTooltip} from "vuetify/components";
import Clock from "@/components/Clock.vue";
import CopyPlaylistDialog from "@/components/dialogs/CopyPlaylistDialog.vue";
import EditExternalPlaylistEventDialog from "@/components/dialogs/EditExternalPlaylistEventDialog.vue";
import GeneralConfirmation from "@/components/dialogs/GeneralConfirmation.vue";
import Loader from "@/components/Loader.vue";
import NoStationSelectedContainer from "@/components/NoStationSelectedContainer.vue";
import PlaylistDateTimePicker from "@/components/PlaylistDateTimePicker.vue";
import PlaylistEmptyContainer from "@/components/PlaylistEmptyContainer.vue";
import PlaylistEvent from "@/components/PlaylistEvent.vue";
import PlaylistLoadSpotsMenuItem from "@/components/PlaylistLoadSpotsMenuItem.vue";
import PlaylistResolveTagsMenuItem from "@/components/PlaylistResolveTagsMenuItem.vue";
import PlaylistTopOfTheHour from "@/components/PlaylistTopOfTheHour.vue";
import Toolbar from "@/components/Toolbar.vue";
import ShowAudioFeedsDialog from "@/components/dialogs/ShowAudioFeedsDialog.vue";

/* Enums */
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import EtmTypes from "@/enums/EtmTypes";
import PlaylistTypes from "@/enums/PlaylistTypes";
import ShowIntegrationType from "@/enums/ShowIntegrationType";
import SyndicationTypes from "@/enums/SyndicationTypes";
import PlaylistFirstVisibleElement from "@/enums/PlaylistFirstVisibleElement";

/* Mixins */
import CutCopyPaste from "@/mixins/CutCopyPaste";
import DragAndDropMixin from "@/mixins/DragAndDrop";
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import PanelPropertiesMixin from "@/mixins/PanelProperties";

// list of handlers to scroll the element
const defaultScrollbarHandlers = ["click-rail", "drag-thumb", "keyboard", "wheel", "touch"];

const PLAYLIST_EVENT_TYPES = [
	EventTypes.PlayAsset,
	EventTypes.SpotBlock,
	EventTypes.DVTBlock,
	EventTypes.ETM,
	EventTypes.Rejoin,
	EventTypes.LocalBreak,
	EventTypes.Comment,
	EventTypes.ExecuteCommand,
	EventTypes.MusicFillPos
];

const PANEL_MIN_WIDTH = 800;

export default {
	name: "Playlist",

	components: {
		Clock,
		CopyPlaylistDialog,
		EditExternalPlaylistEventDialog,
		GeneralConfirmation,
		Loader,
		NoStationSelectedContainer,
		PlaylistEmptyContainer,
		PlaylistEvent,
		PlaylistTopOfTheHour,
		ShowAudioFeedsDialog,
		Toolbar,
		draggable: VueDraggableNext
	},

	mixins: [CutCopyPaste, DragAndDropMixin, DurationFormatMixin, PanelPropertiesMixin],

	props: ["params"],

	data() {
		return {
			loading: true,
			selectedDateTime: null,
			selectedYear: null,
			selectedMonth: null,
			toolbarEpisode: null,
			externalPlaylistFound: false,
			currentOrSelectedShowEpisode: null,
			draggedEvent: null,
			dropTargetEvent: null,
			componentName: "playlist",
			manualScrolling: false,
			clockTabHeight: null,
			editExternalPlaylistEventDialog: {
				open: false,
				options: {}
			},
			showAudioFeedsDialog: {
				open: false,
				options: {}
			},
			isCopyPlaylistDialogOpen: false,
			mousePosition: {
				x: null,
				y: null
			},
			pendingStartEndShow: {pending: false, process: null},
			datePickerOpen: false,
			timePickerOpen: false,
			toolsMenuOpen: false,
			overrideSegueTransitionsButtonOptions: {
				agreeBtn: {
					enabled: true,
					message: "Yes"
				},
				cancelBtn: {
					enabled: true,
					message: "No"
				}
			},
			playlistScrollbarOptions: {
				handlers: defaultScrollbarHandlers
			}
		};
	},

	computed: {
		toolbarItems() {
			if (!this.selectedStation) {
				return [];
			}

			return [
				{
					component: PlaylistDateTimePicker,
					props: {
						selectedStationID: this.selectedStationID,
						datePickerOpen: this.datePickerOpen,
						timePickerOpen: this.timePickerOpen,
						selectedDateTime: this.selectedDateTime.startOf("hour"),
						timezone: this.stationTimezone,
						loading: this.loading
					},
					actions: {
						datePickerClicked: open => {
							this.datePickerOpen = open;
							this.timePickerOpen = false;
						},
						onUpdateMonth: month => {
							this.selectedMonth = month;

							this.loadPlaylistsDatePicker({year: this.selectedYear, month: this.selectedMonth});
						},
						onUpdateYear: year => {
							this.selectedYear = year;

							this.loadPlaylistsDatePicker({year: this.selectedYear, month: this.selectedMonth});
						},
						onDayClicked: date => {
							this.datePickerOpen = false;

							const time = this.selectedDateTime.format("HH:mm");
							const dateTime = moment.tz(`${date.format("YYYY-MM-DD")} ${time}`, this.stationTimezone);
							this.loadSelectedPlaylist(dateTime, false, true);
						},
						timePickerClicked: open => {
							this.timePickerOpen = open;
							this.datePickerOpen = false;
						},
						onTimeClicked: time => {
							this.timePickerOpen = false;

							const date = this.selectedDateTime.format("YYYY-MM-DD");
							const datetime = moment.tz(`${date} ${time}`, this.stationTimezone);
							this.loadSelectedPlaylist(datetime);
						}
					}
				},
				{
					component: VTooltip,
					props: {
						text: "Home",
						location: "bottom",
						openDelay: 1000
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: VBtn,
									tooltip: {
										text: "Home",
										openDelay: 1000
									},
									props: {
										...data.props,
										icon: "mdi-home-circle",
										variant: "text",
										class: "ml-3",
										size: 40,
										elevation: 1,
										disabled: this.loading,
										style: {"border-radius": "3px"}
									},
									actions: {
										click: () => {
											this.scrollToNextInPlaylist();
										}
									}
								}
							]
						}
					]
				},
				{
					component: VMenu,
					props: {
						modelValue: this.toolsMenuOpen
					},
					slots: [
						{
							name: "activator",
							items: data => {
								return [
									{
										component: VBtn,
										props: {
											...data.props,
											icon: "mdi-tools",
											variant: "text",
											class: "ml-3",
											size: 40,
											elevation: 1,
											disabled: this.loading,
											style: {"border-radius": "3px"}
										}
									}
								];
							}
						},
						{
							name: "default",
							items: [
								{
									component: VList,
									props: {
										class: "py-0",
										elevation: 2,
										rounded: true
									},
									slots: [
										{
											name: "default",
											items: [
												{
													component: VListItem,
													props: {
														title: "Copy",
														prependIcon: "mdi-content-copy",
														class: "pr-0"
													},
													actions: {
														click: () => {
															this.isCopyPlaylistDialogOpen = true;
														}
													}
												},
												{
													component: PlaylistLoadSpotsMenuItem,
													props: {
														stationDateTime: this.stationDateTime
													},
													actions: {
														loadSpotsCurrentHour: this.loadSpotsCurrentHour,
														loadSpotsCurrentDay: this.loadSpotsCurrentDay,
														loadSpotsCustomRange: this.loadSpotsCustomRange
													}
												},
												{
													component: PlaylistResolveTagsMenuItem,
													props: {
														stationDateTime: this.stationDateTime
													},
													actions: {
														resolveTagsCurrentHour: this.resolveTagsCurrentHour,
														resolveTagsCurrentDay: this.resolveTagsCurrentDay,
														resolveTagsCustomRange: this.resolveTagsCustomRange
													}
												}
											]
										}
									]
								}
							]
						}
					]
				},
				{
					component: "b",
					text: "Syncing...",
					props: {
						class: {
							"d-none": !this.syncActive || this.$vuetify.display.mdAndDown,
							"ml-3": true,
							"text-red": true
						},
						style: {
							"font-size": "larger"
						}
					}
				}
			];
		},

		headerItems() {
			if (!this.selectedStation || !this.toolbarEpisode) {
				return [];
			}

			return [
				{
					component: VBtn,
					props: {
						class: "ml-1 pa-0 playlist-title-btn",
						variant: "text",
						density: "compact",
						text: this.toolbarEpisode.title,
						color: this.toolbarEpisode.color
					},
					actions: {
						click: this.openShowAudioFeedsDialog
					}
				},
				{
					...(this.displayStartShowBtn && {
						component: VBtn,
						props: {
							class: "ml-2",
							variant: "outlined",
							density: "compact",
							color: "primary",
							disabled: this.pendingStartEndShow.pending,
							text: "Start Show"
						},
						actions: {
							click: this.startShowPlaylist
						}
					})
				},
				{
					...(this.displayEndShowBtn && {
						component: VBtn,
						props: {
							class: "ml-2",
							variant: "outlined",
							density: "compact",
							color: "primary",
							disabled: this.pendingStartEndShow.pending,
							text: "End Show"
						},
						actions: {
							click: this.endShowPlaylist
						}
					})
				},
				{
					...(this.currentOrSelectedShowEpisode.isEditable && {
						component: VBtn,
						props: {
							icon: "mdi-pencil",
							class: "ml-2",
							variant: "text",
							density: "compact",
							color: "primary",
							size: 24,
							style: {"font-size": "12px"},
							disabled: this.pendingStartEndShow.pending
						},
						actions: {
							click: this.openEditExternalPlaylistEventDialog
						}
					})
				},
				{
					...((this.externalPlaylistFound || this.returnToPlayingSpecialShow) && {
						component: VBtn,
						outerClass: "ml-auto",
						props: {
							color: "#e22620",
							variant: "elevated",
							text: "Return To Live Playlist"
						},
						actions: {
							click: this.returnToCurrentPlaylist
						}
					})
				}
			];
		},

		studioSettings() {
			return this.$store.getters.getStudioSettings();
		},

		assetTypes() {
			return this.$store.getters.getAssetTypes();
		},

		currentPlaylist() {
			return this.loadedPlaylists.find(p =>
				this.stationCurrentHour.isSame(moment.tz(p.ScheduledStartDateTime, this.stationTimezone))
			);
		},

		playlistsEvents() {
			// Load the events of the Playlist's module subscribed playlists
			const playlists = this.loadedPlaylists;
			if (!playlists.length) {
				return [];
			}
			// Returns a flattened array of all events from the loaded playlists
			return playlists.flatMap(p => p.Events);
		},

		playlistsList() {
			return this.getPlaylistsList();
		},

		loadedPlaylists() {
			const subscriptions = this.subscriptions;
			if (!subscriptions.length) {
				return [];
			}
			const subscribedPlaylistIDs = new Set(subscriptions.map(s => s.PlaylistID));
			return this.$store.getters
				.loadedSchedulePlaylistsByStationID(this.selectedStationID)
				.filter(p => subscribedPlaylistIDs.has(p.PlaylistID));
		},

		loadedPlaylistsMap() {
			return new Map(this.loadedPlaylists.map(p => [p.PlaylistID, p]));
		},

		emptyPlaceholderPlaylists() {
			return this.$store.getters.getEmptyPlaylists(this.selectedStationID);
		},

		subscriptions() {
			return this.$store.getters.playlistViewSubscriptions(this.selectedStationID, this.$.uid);
		},

		nowPlayingEvent() {
			return (
				_.findLast(
					this.$store.getters.getSequencerItems(this.selectedStationID),
					e => e.StatusCode === EventStatus.Current
				) ?? _.findLast(this.playlistsEvents, e => e.StatusCode === EventStatus.Current)
			);
		},

		firstReadyToPlayItem() {
			return this.playlistsEvents.find(e => e.StatusCode === EventStatus.Ready);
		},

		sequencer() {
			return this.$store.getters.getSequencer(this.selectedStationID);
		},

		sequencerShowEpisodeID() {
			return this.$store.getters.getSequencerShowEpisodeID(this.selectedStationID);
		},

		syncActive() {
			return this.$store.getters.getSyncToSelectionActive();
		},

		etmWithGapTime() {
			const etmWithGapTime = this.currentPlaylist?.Events?.find(
				e => e.EventType === EventTypes.ETM && e.EtmType !== EtmTypes.Hit && e.GapTime
			);

			return this.$store.getters.getSequencerItemByEventID(etmWithGapTime?.EventID) ?? etmWithGapTime;
		},

		gapTimeText() {
			const etm =
				this.etmWithGapTime ??
				this.sequencer?.Items?.find(i => i.EventType === EventTypes.ETM && i.EtmType !== EtmTypes.Hit);
			const gapTime = etm?.GapTime ?? this.sequencer?.GapTime;

			if (gapTime === undefined || gapTime === null) {
				return "";
			}

			let label = "Gap: ";
			if (etm) {
				const type = Object.keys(EtmTypes).find(key => EtmTypes[key] === etm.EtmType);
				const start = etm.StartDateTime ?? etm.AirStartDateTime;
				const timeStr = start ? moment.tz(start, this.stationTimezone).format("HH:mm:ss") : "";
				label = `${type} ETM: ${timeStr}\nActual Gap: `;
			}

			return `${label}${gapTime > 0 ? "+" : ""}${this.secondsToFormat(gapTime, "hh:mm:ss", {
				trim: "left",
				stopTrim: "m"
			})}`;
		},

		leftNavIsOpen() {
			return this.$store.getters.leftNavIsOpen();
		},

		smallScreen() {
			return this.$vuetify.display.lgAndDown || (this.leftNavIsOpen && this.$vuetify.display.xlAndDown);
		},

		displayStartShowBtn() {
			const show = this.currentOrSelectedShowEpisode?.show;
			return (
				show?.special &&
				(show?.integration_type === ShowIntegrationType.Playlist ||
					(show?.integration_type === ShowIntegrationType.Originated &&
						this.selectedStation?.SyndicationType === SyndicationTypes.SyndicationOrigination)) &&
				this.currentOrSelectedShowEpisode?.isAboutToStart &&
				!this.currentOrSelectedShowEpisode?.nowPlaying
			);
		},

		displayEndShowBtn() {
			const show = this.currentOrSelectedShowEpisode?.show;
			return (
				show?.special &&
				show?.integration_type !== ShowIntegrationType.Originated &&
				this.currentOrSelectedShowEpisode?.nowPlaying
			);
		},

		sequencerStartStopPlaylistStatus() {
			return this.$store.getters.sequencerStartStopPlaylistStatus(this.selectedStationID);
		},

		returnToPlayingSpecialShow() {
			const showEpisodeID = this.sequencerShowEpisodeID;
			return (
				this.currentOrSelectedShowEpisode?.isSpecialShow &&
				!this.loadedPlaylists.some(p => p.ShowEpisodeID === showEpisodeID)
			);
		}
	},

	watch: {
		selectedDateTime: {
			handler(dateTime) {
				if (dateTime) {
					this.selectedYear = dateTime.year();
					this.selectedMonth = dateTime.month();
					this.loadPlaylistsDatePicker({year: this.selectedYear, month: this.selectedMonth});
				}
			},
			immediate: true
		},

		selectedStation: {
			async handler(station, prevStation) {
				if (station) {
					if (!prevStation) {
						this.selectedDateTime = this.stationCurrentHour;
					}
					if (station.StationID !== prevStation?.StationID) {
						this.$store.commit("incrementStationPlaylistModuleCounter", station.StationID);
						this.datePickerOpen = false;
						this.timePickerOpen = false;
						this.toolsMenuOpen = false;

						this.loadCurrentPlaylist(false);
					}
					this.externalPlaylistFound = false;
				}

				if (prevStation) {
					this.$store.commit("clearStationPlaylists", prevStation.StationID);
				}
			},
			immediate: true
		},

		"nowPlayingEvent.EventID"(currId, prevId) {
			if (!currId || !this.$refs.scrollbar) {
				return;
			}
			const container = this.$refs.scrollbar.$el;
			const containerRect = container.getBoundingClientRect();

			const elementInFirstPosition = document.elementFromPoint(containerRect.left, containerRect.top);

			const elementInFirstPositionID = elementInFirstPosition.closest(".playlist-event")?.id; // Get the id of the top visible element

			if (
				this.studioSettings?.preferences?.playlist_first_visible_item ===
				PlaylistFirstVisibleElement.NextAfterNowPlaying.val
			) {
				if (elementInFirstPositionID === currId) {
					this.scrollToNextInPlaylist();
				}
			} else {
				if (elementInFirstPositionID === prevId) {
					this.scrollToNextInPlaylist();
				}
			}
		},

		loadedPlaylists(playlists, previous) {
			// In case we show an external playlist that was just deleted
			if (!this.loading && this.externalPlaylistFound && playlists.length === 0 && previous.length > 0) {
				this.returnToCurrentPlaylist();
			}
		},

		stationCurrentHour: {
			handler(newDateTime, oldDateTime) {
				// Check if the hour just changed and select the new one if the previous was selected
				if (!newDateTime.isSame(oldDateTime) && this.selectedDateTime?.isSame(oldDateTime)) {
					this.selectedDateTime = newDateTime;
				}
			},
			immediate: true
		},

		"currentPlaylist.HotlineSequence"(currentValue, previousValue) {
			if (
				!this.$refs.scrollbar ||
				this.externalPlaylistFound ||
				_.isEqual(currentValue, previousValue) ||
				Math.abs(this.stationCurrentHour.diff(this.selectedDateTime, "hours")) > 1
			) {
				return;
			}

			let scrollToNowPlayingEvent = true;

			// Check if the now playing event is visible in the playlist container
			// if not, then it means that the user is interacting with the playlist so do not scroll to the new now playing event
			// if yes, scroll to the new now playing event
			const container = this.$refs.scrollbar.$el;
			const nowPlayingEventEl = container.querySelector(".playlist-event.now-playing");

			if (nowPlayingEventEl) {
				const containerRect = container.getBoundingClientRect();
				const nowPlayingEventElRect = nowPlayingEventEl.getBoundingClientRect();

				scrollToNowPlayingEvent =
					nowPlayingEventElRect.top <= containerRect.top
						? containerRect.top - nowPlayingEventElRect.top <= nowPlayingEventElRect.height
						: nowPlayingEventElRect.bottom - containerRect.bottom <= nowPlayingEventElRect.height;
			}

			if (scrollToNowPlayingEvent) {
				this.scrollToNextInPlaylist();
			}
		},

		sequencerStartStopPlaylistStatus(current, previous) {
			if (!current.pending && previous.pending && this.pendingStartEndShow.pending) {
				if (current.statusCode === 200) {
					this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
						message: `Live Show ${this.pendingStartEndShow.process}ed`
					});
				} else {
					if (current.message) {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {message: current.message});
					}
				}
				this.pendingStartEndShow = {pending: false, process: null};
			}
		},

		"studioSettings.preferences.playlist_first_visible_item"() {
			if (this.playlistsEvents.length > 0) {
				this.scrollToNextInPlaylist();
			}
		}
	},

	created() {
		this.debouncedOnScrollY = _.debounce(this.onScrollY, 50);

		this.$store.commit("setContainerByRef", {ref: "PlaylistComponent", component: this});

		emitter.on("playlist-set-dragged-event", this.setDraggedEvent);
		emitter.on("playlist-set-dragged-clone", this.handleDraggedClone);
		emitter.on("playlist-set-drop-target-event", this.setDropTargetEvent);
		emitter.on("playlist-on-drop-hotkey-event", this.onDropHotkeyEvent);
		emitter.on("calendar:loadPlaylist", this.loadPlaylistByShowEpisodeId);
		emitter.on("toggle-playlist-context-menu", this.togglePlaylistScrolling);

		document.addEventListener("keydown", this.handleKeyboardEvents);
		document.addEventListener("mousemove", this.handleMouseMovement);

		// This event fires with a visibilityState of hidden when a user navigates to a new page, switches tabs,
		// closes the tab, minimizes or closes the browser
		document.addEventListener("visibilitychange", this.visibilitychange);
	},

	mounted() {
		// Constraints belong to the group, so reapply them whenever the panel is moved into another one.
		this.updateMinWidth();
		this.groupChangeDisposable = this.params?.api?.onDidGroupChange(this.updateMinWidth);

		this.$nextTick(this.setHeights);

		emitter.on("docking:layoutChanged", this.setHeights);

		window.addEventListener("resize", this.setHeights);
	},

	beforeUnmount() {
		this.groupChangeDisposable?.dispose();

		this.$store.commit("clearStationPlaylists", this.selectedStationID);

		emitter.off("playlist-set-dragged-event", this.setDraggedEvent);
		emitter.off("playlist-set-dragged-clone", this.handleDraggedClone);
		emitter.off("playlist-set-drop-target-event", this.setDropTargetEvent);
		emitter.off("playlist-on-drop-hotkey-event", this.onDropHotkeyEvent);
		emitter.off("calendar:loadPlaylist", this.loadPlaylistByShowEpisodeId);
		emitter.off("toggle-playlist-context-menu", this.togglePlaylistScrolling);

		emitter.off("docking:layoutChanged", this.setHeights);

		this.$store.commit("unsetContainerByRef", "PlaylistComponent");

		document.removeEventListener("keydown", this.handleKeyboardEvents);
		document.removeEventListener("mousemove", this.handleMouseMovement);

		document.removeEventListener("visibilitychange", this.visibilitychange);

		window.removeEventListener("resize", this.setHeights);
	},

	methods: {
		hourClicked(dateTime) {
			if (!this.loading) {
				this.loadSelectedPlaylist(dateTime, false, true);
			}
		},

		loadPlaylistsByTimeRange(from, to, callUnsubscribePlaylists, isPrevious) {
			return this.$store
				.dispatch("getPlaylistWithAssetsBySchedule", {stationID: this.selectedStationID, from, to})
				.then(playlists => {
					// This code section will be executed on load next/previous playlist only
					if (callUnsubscribePlaylists) {
						const hourFrom = isPrevious ? from : from.clone().subtract(2, "hour");
						const hourTo = isPrevious ? from.clone().add(2, "hour") : from;

						const playlistsToKeep = [];
						// Iterate through dateTime - 1 hour, dateTime and dateTime + 1 hour
						for (const dt = hourFrom; dt.isSameOrBefore(hourTo); dt.add(1, "hour")) {
							const pl = this.findPlaylistByDateTime(dt);
							if (pl) {
								//If the playlist is part of an episode and an episode has already loaded, keep in the "subscription" all the episode playlists
								if (pl.ShowEpisodeID) {
									const playlists =
										this.$store.getters.loadedShowEpisodePlaylists(
											this.selectedStationID,
											pl.ShowEpisodeID
										)?.playlists ?? [];

									playlistsToKeep.push(...playlists);
								} else {
									playlistsToKeep.push(pl.PlaylistID);
								}
							}
						}

						this.unsubscribePlaylists(
							this.loadedPlaylists.filter(sub => !playlistsToKeep.includes(sub.PlaylistID))
						);
					}

					for (let dt = from.clone(); dt.isSameOrBefore(to); dt.add(1, "hour")) {
						let playlist = playlists.find(pl => pl.ScheduledStartDateTime === dt.toISOString());
						if (!playlist) {
							const PlaylistID = `empty-playlist-${uuid.v1()}`;
							playlist = {
								PlaylistID,
								StationID: this.selectedStationID,
								ScheduledStartDateTime: dt.tz(this.stationTimezone).toISOString(),
								Events: [],
								TopOfTheHour: {
									PlaylistID,
									AirStartDateTime: dt.tz(this.stationTimezone).toISOString(),
									EventID: `top-of-the-hour-${PlaylistID}`
								},
								isEmptyPlaceholder: true
							};
							this.$store.commit("addEmptyPlaylist", playlist);
						} else if (_.isEmpty(playlist?.Events)) {
							this.$store.commit("addEmptyPlaylist", playlist);
						}
					}
					return playlists;
				});
		},

		loadPlaylistsFromExternalSource(showEpisodeID) {
			return this.$store
				.dispatch("getPlaylistWithAssetsByShowEpisodeID", {stationID: this.selectedStationID, showEpisodeID})
				.then(playlists => playlists)
				.catch(error => {
					console.log(error);
					return [];
				});
		},

		async loadSelectedPlaylist(dateTime, loadMore, goToSelectedPlaylist) {
			this.loading = true;
			dateTime.tz(this.stationTimezone, true); // Keep local time, change timezone

			if (!loadMore) {
				this.selectedDateTime = dateTime.clone();
			}

			const playlistsHoursToLoad = [];
			const loadedPlaylistsInTimeRange = [];

			const hourFrom = dateTime.isBefore(this.stationCurrentHour.clone().subtract(24, "hours")) // if selected dateTime is before the -24 hours limit we have
				? dateTime.clone().add(1, "hour") // add 1 hour
				: dateTime.isAfter(this.stationCurrentHour.clone().subtract(24, "hours")) // else if selected dateTime is after the -24 limit we have
					? dateTime.clone().subtract(1, "hour") // get the previous hour
					: dateTime.clone(); // else start from the selected dateTime

			const hourTo = loadMore ? dateTime.clone() : dateTime.clone().add(1, "hour"); // if loadMore is true, set max the selected dateTime to load only one playlist, else selected dateTime + 1 hour

			// Iterate through dateTime - 1 hour, dateTime and dateTime + 1 hour
			for (let dt = hourFrom.clone(); dt.isSameOrBefore(hourTo); dt.add(1, "hour")) {
				const playlist = this.findPlaylistByDateTime(dt.clone());
				if (!playlist) {
					playlistsHoursToLoad.push(dt.clone()); // keep the dateTime if no playlist
				} else {
					loadedPlaylistsInTimeRange.push(playlist); // keep the playlist of the already loaded playlist
				}
			}

			// if there are hours in the selected dateTimes range, load the playlists for these hours
			if (playlistsHoursToLoad.length) {
				const from = _.first(playlistsHoursToLoad); // first hour in the generated hours array
				const to = _.last(playlistsHoursToLoad); // last hour in the generated hours array

				loadedPlaylistsInTimeRange.push(
					...(await this.loadPlaylistsByTimeRange(from, to, false, false)) // keep the playlist of the already loaded playlist
				);
			}

			for (const playlist of _.clone(loadedPlaylistsInTimeRange)) {
				if (playlist.ShowEpisodeID) {
					const episodePlaylists = await this.getEpisodePlaylists(playlist);
					episodePlaylists.forEach(p => {
						if (!loadedPlaylistsInTimeRange.find(id => id === p.PlaylistID)) {
							loadedPlaylistsInTimeRange.push(p);
						}
					});
				}
			}

			if (!loadMore) {
				const playlistIDs = _.map(loadedPlaylistsInTimeRange, "PlaylistID");
				// Unsubscribe from the playlists that belong to different hours than the selected dateTime +-1 hour
				await this.unsubscribePlaylists(this.loadedPlaylists.filter(p => !playlistIDs.includes(p.PlaylistID)));
			}

			// There are cases that more than 1 playlist are returned for the same hour. Get the first in the list
			await this.subscribePlaylists(loadedPlaylistsInTimeRange);

			// For some reason, nextTick doesn't work
			this.$worker.setTimeout(() => {
				if (!this.$refs.scrollbar) {
					this.loading = false;
					return;
				}
				// Check if the playlist container has empty space and that the to dateTime doesn't exceed the 24hours limit we have
				const playlistContainer = this.$refs.scrollbar.$el;
				const playlistContainerHeight = playlistContainer.clientHeight;
				const playlistEventsTotalHeight = playlistContainer.firstChild.clientHeight;
				const hoursDiff = moment
					.duration(this.stationCurrentHour.clone().add(24, "hours").diff(dateTime))
					.asHours();

				if (playlistContainerHeight - playlistEventsTotalHeight > 0 && hoursDiff > 0) {
					// check if the playlist container has empty space and that the to dateTime doesn't exceed the 24hours limit we have
					dateTime = dateTime.clone().add(1, "hour");
					loadMore = true; // keep in variable to handle in the final method
					this.loadSelectedPlaylist(dateTime, loadMore);
					return false;
				}
				if (goToSelectedPlaylist) {
					this.scrollToTopOfSelectedPlaylist();
				} else if (dateTime.isSame(this.stationCurrentHour)) {
					this.scrollToNextInPlaylist();
				} else {
					const selectedPlaylist = this.findPlaylistByDateTime(dateTime);
					if (selectedPlaylist) {
						const container = this.$refs.scrollbar.$el;
						let el = container.querySelector(`[id="top-of-the-hour-${selectedPlaylist.PlaylistID}"]`);

						// If there is not a "Top of Hour" event, e.g. playlist comes from a Show Episode
						if (!el) {
							// Find the first event of the selected playlist
							for (const e of selectedPlaylist.Events) {
								el = container.querySelector(`[id="${e.EventID}"]`);
								if (el) {
									break;
								}
							}
						}

						this.scrollTo(el.offsetTop - container.offsetTop);
					}
				}
				this.loading = false;
			}, 0);
		},

		async loadPreviousPlaylist() {
			const playlists = [];
			const dateTime = this.selectedDateTime.clone().subtract(1, "hour");

			if (!dateTime.isBefore(this.stationCurrentHour.clone().subtract(24, "hours"))) {
				// check that the to dateTime doesn't exceed the 24hours limit we have
				const playlist = this.findPlaylistByDateTime(dateTime);

				if (playlist) {
					// playlist already loaded (e.g. from the Log module)
					playlists.push(playlist);
				} else {
					this.loading = true;
					try {
						playlists.push(...(await this.loadPlaylistsByTimeRange(dateTime, dateTime, true, true)));
					} catch (e) {
						console.error("Error loadPreviousPlaylist", e);
					}
				}

				for (const playlist of _.clone(playlists)) {
					if (playlist.ShowEpisodeID) {
						const episodePlaylists = await this.getEpisodePlaylists(playlist);
						episodePlaylists.forEach(p => {
							if (!playlists.find(({PlaylistID}) => PlaylistID === p.PlaylistID)) {
								playlists.push(p);
							}
						});
					}
				}

				// There are cases that more than 1 playlist are returned for the same hour. Get the first in the list
				this.subscribePlaylists(_.uniqBy(playlists, "ScheduledStartDateTime"));

				// For some reason, nextTick doesn't work
				this.$worker.setTimeout(() => {
					let length = 0;
					playlists.forEach(pl => {
						length += this.playlistsList.find(p => p.PlaylistID === pl.PlaylistID)?.Events?.length ?? 0;
					});

					this.scrollTo((length + 1) * 50);

					// in the case of empty playlist, onScrollY() is not getting triggered, so we need to update the selectedDateTime manually
					if (!length) {
						this.selectedDateTime = dateTime;
					}
					this.loading = false;
				}, 0);
			}
		},

		async loadNextPlaylist() {
			const playlists = [];
			let dateTime = this.selectedDateTime.clone().add(1, "hour");

			if (!dateTime.isAfter(this.stationCurrentHour.clone().add(24, "hours"))) {
				// check that the to dateTime doesn't exceed the 24hours limit we have
				const playlist = this.findPlaylistByDateTime(dateTime);

				if (playlist) {
					// playlist already loaded (e.g. from the Log module)
					playlists.push(playlist);
				} else {
					this.loading = true;
					try {
						playlists.push(...(await this.loadPlaylistsByTimeRange(dateTime, dateTime, true, false)));
					} catch (e) {
						console.error("Error loadNextPlaylist", e);
					}
				}

				for (const playlist of _.clone(playlists)) {
					if (playlist.ShowEpisodeID) {
						const episodePlaylists = await this.getEpisodePlaylists(playlist);
						episodePlaylists.forEach(p => {
							if (!playlists.find(({PlaylistID}) => PlaylistID === p.PlaylistID)) {
								playlists.push(p);
							}
						});
					}
				}

				// There are cases that more than 1 playlist are returned for the same hour. Get the first in the list
				await this.subscribePlaylists(_.uniqBy(playlists, "ScheduledStartDateTime"));

				//For some reason, nextTick doesn't work
				this.$worker.setTimeout(() => {
					const container = this.$refs.scrollbar.$el;
					const containerHeight = container?.getBoundingClientRect()?.height;

					const playlistToScroll = this.playlistsList.find(p =>
						moment(p.ScheduledStartDateTime).isBetween(dateTime, dateTime.clone().add(1, "h"), null, "[)")
					);
					const elToScroll = container.querySelector(`[data-playlist-id="${playlistToScroll?.PlaylistID}"]`);
					if (elToScroll) {
						this.scrollTo(
							elToScroll.offsetTop + elToScroll.getBoundingClientRect().height - containerHeight
						);
					}
					this.loading = false;
				}, 0);
			}
		},

		async getEpisodePlaylists({ShowEpisodeID}, recalculate = false, callUnsubscribe = false) {
			const loadedEpisode = this.$store.getters.loadedShowEpisodePlaylists(this.selectedStationID, ShowEpisodeID);
			const episodePlaylists = [];
			if (loadedEpisode) {
				const playlistsToLoad = [];
				for (const playlist_id of loadedEpisode.playlists) {
					const playlist = this.$store.getters.getPlaylistById({
						stationID: this.selectedStationID,
						playlistID: playlist_id
					});
					if (playlist) {
						episodePlaylists.push(playlist);
					} else {
						playlistsToLoad.push(playlist_id);
					}
				}

				if (playlistsToLoad.length < loadedEpisode.playlists.length) {
					for (const playlist_id of playlistsToLoad) {
						episodePlaylists.push(
							await this.$store.dispatch("getPlaylistWithAssetsById", {playlist_id, recalculate})
						);
					}
				} else {
					episodePlaylists.push(...(await this.loadPlaylistsFromExternalSource(ShowEpisodeID)));
				}
			} else {
				episodePlaylists.push(...(await this.loadPlaylistsFromExternalSource(ShowEpisodeID)));
			}

			if (episodePlaylists.length > 0) {
				this.$store.commit("setLoadedShowEpisodePlaylist", {
					stationID: this.selectedStationID,
					showEpisodeID: ShowEpisodeID,
					playlists: episodePlaylists
				});

				// There are cases that more than 1 playlist are returned for the same hour. Get the first in the list
				this.subscribePlaylists(_.uniqBy(episodePlaylists, "ScheduledStartDateTime"));
				if (callUnsubscribe) {
					const newPlaylistIDs = _.map(episodePlaylists, "PlaylistID");
					this.unsubscribePlaylists(this.loadedPlaylists.filter(p => !newPlaylistIDs.includes(p.PlaylistID)));
				}
			}
			return episodePlaylists;
		},

		findPlaylistByDateTime(dateTime) {
			return this.$store.getters.getPlaylistByDateTime(this.selectedStationID, dateTime);
		},

		getPlaylistsList() {
			const playlistsList = this.loadedPlaylists.map(p => {
				const playlist = {
					PlaylistID: p.PlaylistID,
					ScheduledStartDateTime: p.ScheduledStartDateTime,
					Events: p.Events.filter(e => PLAYLIST_EVENT_TYPES.includes(e.EventType))
				};

				if (!p.ShowEpisodeID) {
					playlist.TopOfTheHour = {
						PlaylistID: p.PlaylistID,
						EventID: `top-of-the-hour-${p.PlaylistID}`,
						TopOfTheHour: true,
						AirStartDateTime: p.ScheduledStartDateTime,
						projectedTime: this.calculatePlaylistProjectedTime(p.ScheduledStartDateTime)
					};
				}

				return playlist;
			});

			// We need to know the events that are before and after the TOH indicator to use them in case of
			// dragging and dropping an event over a TOH
			playlistsList.forEach((playlist, index) => {
				if (playlist.TopOfTheHour) {
					playlist.TopOfTheHour.previousEvent = _.findLast(playlistsList[index - 1]?.Events);
					playlist.TopOfTheHour.nextEvent = playlist.Events[0];
				}
			});
			playlistsList.push(...this.emptyPlaceholderPlaylists.filter(p => p.isEmptyPlaceholder));

			return _.sortBy(playlistsList, a => a.ScheduledStartDateTime);
		},

		calculatePlaylistProjectedTime(TOHDateHour, playlistsEvents = this.playlistsEvents) {
			let projectedTime = null;

			// get last valid event of the previous hour
			const previousHour = moment(TOHDateHour).clone().subtract(1, "hour").hour();
			const previousHourLastEvent = _.findLast(playlistsEvents, e => {
				return (
					e.AirStartDateTime &&
					e.StatusCode &&
					![EventStatus.PendingNotPlayed, EventStatus.NotPlayed, EventStatus.EventError].includes(
						e.StatusCode
					) &&
					moment(e.AirStartDateTime).hour() === previousHour
				);
			});

			if (previousHourLastEvent) {
				let previousHourLastEventDuration = 0;
				if (previousHourLastEvent.EventType === EventTypes.PlayAsset) {
					previousHourLastEventDuration = previousHourLastEvent.Asset?.POIMarkup?.Length ?? 0;
				} else {
					previousHourLastEvent?.ChildPlaylist?.Events.forEach(e => {
						previousHourLastEventDuration += e.Asset?.POIMarkup?.Length ?? 0;
					});
				}

				projectedTime = moment.duration(
					moment(previousHourLastEvent.AirStartDateTime)
						.add(previousHourLastEventDuration, "seconds")
						.diff(moment(TOHDateHour), "seconds"),
					"seconds"
				);

				const formattedProjectedTime = this.secondsToFormat(projectedTime, "mm:ss.S", {
					trim: "left",
					stopTrim: "m"
				});

				return `${projectedTime > 0 ? "+" : ""}${formattedProjectedTime}`;
			}
			return projectedTime;
		},

		getCurrentOrFirstReadyInCurrentHourPlaylistEvent() {
			if (
				this.studioSettings?.preferences?.playlist_first_visible_item ===
				PlaylistFirstVisibleElement.NowPlaying.value
			) {
				return this.nowPlayingEvent;
			} else {
				return this.nowPlayingEvent || this.firstReadyToPlayItem;
			}
		},

		scrollToNextInPlaylist() {
			const scrollToEvent = this.getCurrentOrFirstReadyInCurrentHourPlaylistEvent();

			if (scrollToEvent) {
				const scrollToEventEl = this.$refs.scrollbar.$el.querySelector(`[id="${scrollToEvent.EventID}"]`);

				if (scrollToEventEl) {
					this.scrollTo(scrollToEventEl.offsetTop);
				}

				if (!this.stationCurrentHour.isSame(this.selectedDateTime)) {
					this.loadSelectedPlaylist(this.stationCurrentHour);
				}
			} else if (!this.stationCurrentHour?.isSame(this.selectedDateTime)) {
				// In case we are in an hour different from the current one
				this.loadSelectedPlaylist(this.stationCurrentHour);
			} else {
				this.scrollToTopOfSelectedPlaylist();
			}
		},

		scrollToTopOfSelectedPlaylist() {
			const selectedHourPlaylist = this.playlistsList.find(
				p =>
					moment(p.ScheduledStartDateTime).isSameOrAfter(this.selectedDateTime) &&
					moment(p.ScheduledStartDateTime).isBefore(this.selectedDateTime.clone().add(1, "h"))
			);

			if (selectedHourPlaylist) {
				const elToScroll = this.$refs.scrollbar.$el.querySelector(
					`[data-playlist-id="${selectedHourPlaylist.PlaylistID}"]`
				);
				if (elToScroll) {
					this.$nextTick(() => this.scrollTo(elToScroll.offsetTop));
				}

				const selectedPlaylistID = selectedHourPlaylist.PlaylistID;
				const playlist = this.loadedPlaylistsMap.get(selectedPlaylistID);
				const dateTime = moment.tz(playlist?.ScheduledStartDateTime, this.stationTimezone);

				if (!this.selectedDateTime || !dateTime.isSame(this.selectedDateTime)) {
					this.selectedDateTime = dateTime;
				}
			}
		},

		scrollTo(top) {
			if (this.$refs.scrollbar) {
				this.manualScrolling = true;
				this.$refs.scrollbar.$el?.scrollTo({
					top: top,
					left: 0
				});
			}
		},

		onScrollY(e) {
			// If we are loading the events from an episode (an external playlist), we should disable the scroll feature.
			if (this.externalPlaylistFound || this.draggedEvent || this.currentOrSelectedShowEpisode?.isSpecialShow) {
				return;
			}
			if (this.playlistsList.length > 0 && !this.loading) {
				// Load previous or next playlist only when user is scrolling the view
				if (!this.manualScrolling) {
					const scrollTop = e.target.scrollTop;
					const container = this.$refs.scrollbar.$el;
					const containerRect = container.getBoundingClientRect();

					const elementInFirstPosition = document.elementFromPoint(containerRect.left, containerRect.top);

					const isTopOfTheHour = !!elementInFirstPosition.closest(".top-of-the-hour");

					// fix selected date time by top of the hour datetime
					const selectedPlaylistID =
						elementInFirstPosition.dataset.playlistId ?? // empty playlist container
						(isTopOfTheHour
							? elementInFirstPosition.closest(".top-of-the-hour")?.dataset?.playlistId
							: elementInFirstPosition.closest(".playlist-event")?.dataset?.playlistId);

					const playlist = this.loadedPlaylistsMap.get(selectedPlaylistID);
					const dateTime = moment.tz(playlist?.ScheduledStartDateTime, this.stationTimezone);

					if (!this.selectedDateTime || !dateTime.isSame(this.selectedDateTime)) {
						this.selectedDateTime = dateTime;
					}

					// Resolve against playlistsList so empty placeholders (kept in a separate store key
					// and absent from loadedPlaylists) don't make the lookup fall back to "now".
					const visiblePlaylist = this.loadedPlaylistsMap.get(selectedPlaylistID);
					if (visiblePlaylist) {
						const dateTime = moment.tz(visiblePlaylist.ScheduledStartDateTime, this.stationTimezone);

						if (!this.selectedDateTime || !dateTime.isSame(this.selectedDateTime)) {
							this.selectedDateTime = dateTime;
						}
					}

					if (scrollTop === 0) {
						// Check if the first visible event in the container belongs in the selected date time playlist
						// If not, set the selectedDateTime to this event's playlist ScheduledStartDateTime
						const topVisibleEventPlaylist = this.playlistsList[0];
						if (
							topVisibleEventPlaylist &&
							!moment(topVisibleEventPlaylist.ScheduledStartDateTime).isSame(this.selectedDateTime)
						) {
							this.selectedDateTime = moment.tz(
								topVisibleEventPlaylist.ScheduledStartDateTime,
								this.stationTimezone
							);
						}

						this.loadPreviousPlaylist();
					} else if (scrollTop + e.target.clientHeight + 1 > e.target.scrollHeight) {
						// Check if the last visible event in the container belongs in the selected date time playlist
						// If not, set the selectedDateTime to this event's playlist ScheduledStartDateTime
						const lastVisibleEventPlaylist = _.findLast(this.playlistsList);
						if (
							lastVisibleEventPlaylist &&
							!moment(lastVisibleEventPlaylist.ScheduledStartDateTime).isSame(this.selectedDateTime)
						) {
							this.selectedDateTime = moment.tz(
								lastVisibleEventPlaylist.ScheduledStartDateTime,
								this.stationTimezone
							);
						}

						this.loadNextPlaylist();
					}
				} else {
					this.manualScrolling = false;
				}
			}
		},

		async subscribePlaylists(playlists) {
			// Playlists
			// The expression this.$.uid is used in Vue 3 to access the unique internal ID of a component instance
			await this.$store.dispatch("addRemovePlaylistSubscriptions", {playlists, componentID: this.$.uid});

			// Spotblocks and local breaks playlists
			for (const playlist of playlists) {
				const spotBlocks = (playlist.Events || []).filter(({EventType}) => EventType === EventTypes.SpotBlock);
				if (spotBlocks.length) {
					const spotBlockPlaylists = _.compact(_.map(spotBlocks, "ChildPlaylist"));
					if (spotBlockPlaylists.length) {
						await this.$store.dispatch("addRemovePlaylistSubscriptions", {
							playlists: spotBlockPlaylists,
							componentID: this.$.uid
						});
					}
				}

				const localBreaks = (playlist.Events || []).filter(
					({EventType}) => EventType === EventTypes.LocalBreak
				);
				if (localBreaks.length) {
					//call "subscribePlaylists" giving the local break playlists to subscribe to the inner spot block playlists too
					const localBreakPlaylists = _.compact(_.map(localBreaks, "ChildPlaylist"));
					if (localBreakPlaylists.length) {
						await this.subscribePlaylists(localBreakPlaylists);
					}
				}
			}
		},

		async unsubscribePlaylists(playlists) {
			const realPlaylists = playlists.filter(p => p.PlaylistID && !p.isEmptyPlaceholder);

			await this.$store.dispatch("addRemovePlaylistSubscriptions", {
				playlists: realPlaylists,
				componentID: this.$.uid,
				subscribe: false
			});

			// Spotblocks and local breaks playlists
			for (const playlist of realPlaylists) {
				const spotBlocks = (playlist.Events || []).filter(({EventType}) => EventType === EventTypes.SpotBlock);
				if (spotBlocks.length) {
					const spotBlockPlaylists = _.compact(_.map(spotBlocks, "ChildPlaylist"));
					if (spotBlockPlaylists.length) {
						await this.$store.dispatch("addRemovePlaylistSubscriptions", {
							playlists: spotBlockPlaylists,
							componentID: this.$.uid,
							subscribe: false
						});
					}
				}

				const localBreaks = (playlist?.Events || []).filter(
					({EventType}) => EventType === EventTypes.LocalBreak
				);
				if (localBreaks.length) {
					//call "unsubscribePlaylists" giving the local break playlists to subscribe to the inner spot block playlists too
					const localBreakPlaylists = _.compact(_.map(localBreaks, "ChildPlaylist"));
					if (localBreakPlaylists.length) {
						await this.unsubscribePlaylists(localBreakPlaylists);
					}
				}
			}
		},

		async loadPlaylistByShowEpisodeId(showEpisode, fromSequencer = false) {
			this.loading = true;
			this.currentOrSelectedShowEpisode = showEpisode;

			const result = await this.getEpisodePlaylists(this.currentOrSelectedShowEpisode, true, true);
			try {
				if (result.length > 0) {
					this.externalPlaylistFound = !fromSequencer;
					this.currentOrSelectedShowEpisode.nowPlaying =
						this.sequencerShowEpisodeID === this.currentOrSelectedShowEpisode.ShowEpisodeID;

					const isSpecialShow = result[0].PlaylistType === PlaylistTypes.SpecialShow;
					let isPast = true;

					if (fromSequencer) {
						const period = {
							from: this.stationDateTime.clone().subtract(1, "days").startOf("day").valueOf(),
							to: this.stationDateTime.clone().add(1, "days").endOf("day").valueOf()
						};
						const event = this.$store.getters.schedulerEventById({
							stationID: this.selectedStationID,
							period,
							eventID: showEpisode.ShowEpisodeID.split("_")[0]
						});

						if (event) {
							this.currentOrSelectedShowEpisode = {
								...this.currentOrSelectedShowEpisode,
								...event,
								schedulerSubscription: this.$store.getters
									.schedulerSubscriptions(this.selectedStationID)
									.find(s => s.object_id === event.object_id)
							};

							isPast = moment(event.start).add(event.duration, "seconds").isBefore(moment());
						}
					} else {
						this.currentOrSelectedShowEpisode = {
							...this.currentOrSelectedShowEpisode,
							schedulerSubscription: this.$store.getters
								.schedulerSubscriptions(this.selectedStationID)
								.find(s => s.object_id === showEpisode.object_id)
						};

						isPast = moment(showEpisode.start).add(showEpisode.duration, "seconds").isBefore(moment());
					}

					this.currentOrSelectedShowEpisode.isSpecialShow = isSpecialShow;
					this.currentOrSelectedShowEpisode.isEditable = isSpecialShow && !isPast;

					if (this.currentOrSelectedShowEpisode.object_id) {
						this.currentOrSelectedShowEpisode.show = await this.$store.dispatch("getShow", {
							stationID: this.selectedStationID,
							showID: this.currentOrSelectedShowEpisode.object_id
						});
						if (this.currentOrSelectedShowEpisode.show) {
							this.currentOrSelectedShowEpisode.show.settings = await this.$store.dispatch(
								"getShowSettings",
								this.currentOrSelectedShowEpisode.object_id
							);
						}
					}

					this.loadHeaderForEventEpisode(
						this.currentOrSelectedShowEpisode.title ?? result[0].Title.split(",")[0], // In case of the sequencer isn't able to retrieve the RealtimeDisplay attribute, so we are collecting the name from the playlist
						this.currentOrSelectedShowEpisode.resourceTitle,
						this.sequencerShowEpisodeID === this.currentOrSelectedShowEpisode.ShowEpisodeID
					);

					if (fromSequencer) {
						this.scrollToNextInPlaylist();
					} else {
						emitter.emit("calendar:playlistLoaded", showEpisode.ShowEpisodeID);
						this.scrollTo(0);
					}
					this.loading = false;
				} else if (fromSequencer) {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "No live playlist playing, loading the top of the hour playlist instead."
					});
					this.loadSelectedPlaylist(this.stationCurrentHour);
				} else {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: `There isn’t a playlist available for the episode: ${this.currentOrSelectedShowEpisode.title}.`
					});
				}
			} catch (e) {
				console.error("Playlist Selector - Error loadPlaylist ", e);
			} finally {
				this.loading = false;
			}
		},

		returnToCurrentPlaylist() {
			emitter.emit("calendar:playlistLoaded", null);
			// If the sequencer object turns out to be null or undefined, we need to do nothing and throw an error message as in the AC 2.a from SS-1655
			if (this.sequencer) {
				this.loadCurrentPlaylist(true);
			} else {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "It wasn’t possible to get the current playlist."
				});
			}
		},

		loadCurrentPlaylist(returnToHourPlaylist = false) {
			// If unable to retrieve the ShowEpisodeID it means that there is no LiveShow running, so it should load the TOH.
			if (this.sequencerShowEpisodeID) {
				this.selectedDateTime = this.stationCurrentHour.clone();
				// We are collecting the title from the sequencer object
				let showEpisode = {
					title: this.sequencer?.RealtimeDisplay?.split(" - ")[0],
					ShowEpisodeID: this.sequencerShowEpisodeID,
					// While collecting the ShowEpisodeID from the Sequencer, we are not able to determine the current Lane that it's playing
					resourceTitle: ""
				};
				this.loadPlaylistByShowEpisodeId(showEpisode, true);
			} else {
				if (returnToHourPlaylist) {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "No live playlist playing, loading the top of the hour playlist instead."
					});
				}
				this.loading = true;
				this.externalPlaylistFound = false;
				this.currentOrSelectedShowEpisode = null;
				this.toolbarEpisode = null;
				this.loadSelectedPlaylist(this.selectedDateTime);
			}
		},

		loadHeaderForEventEpisode(title, resourceTitle, live) {
			if (live) {
				this.toolbarEpisode = {
					title: resourceTitle ? `${title} (${resourceTitle}) LIVE` : `${title} LIVE`,
					color: "#e22100"
				};
			} else {
				this.toolbarEpisode = {
					title: `${title} (${resourceTitle})`.toUpperCase(),
					color: !this.darkMode ? "#0a0b09" : "#e6eaed"
				};
			}
		},

		setHeights() {
			if (!this.$el.offsetParent) {
				return;
			}
			const el = this.$refs.rootCard?.$el;
			this.containerHeight = el.clientHeight - this.$refs.toolbarContainer.$el.offsetHeight;
			this.clockTabHeight = this.containerHeight > 500 ? this.containerHeight / 24 : this.containerHeight / 12;
		},

		visibilitychange() {
			if (!document.hidden) {
				// if while the tab was inactive, the current time went after the last loaded playlist's event, scroll to now playing
				const lastPlaylistEvent = _.findLast(this.playlistsEvents);
				if (lastPlaylistEvent) {
					const lastLoadedEventAirtime = moment.tz(
						_.findLast(this.playlistsEvents).AirStartDateTime,
						this.stationTimezone
					);
					if (this.stationCurrentHour.isAfter(lastLoadedEventAirtime)) {
						if (this.findPlaylistByDateTime(this.stationCurrentHour)) {
							this.scrollToNextInPlaylist();
						} else {
							this.loadSelectedPlaylist(this.stationCurrentHour);
						}
					}
				}
			}
		},

		handleKeyboardEvents(ev) {
			if (ev.ctrlKey && ev.key === "j") {
				ev.preventDefault();
				const hoveredElement = document.elementFromPoint(this.mousePosition.x, this.mousePosition.y);
				const elementID = hoveredElement.closest(".playlist-event")?.id;

				const event = this.playlistsEvents.find(e => e.EventID === elementID);
				if (event) {
					navigator.clipboard.writeText(JSON.stringify(event)).then(() => {
						this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {message: "Event JSON copied"});
					});
				}
			} else if (ev.ctrlKey && ev.key === "i") {
				ev.preventDefault();
				const hoveredElement = document.elementFromPoint(this.mousePosition.x, this.mousePosition.y);
				const elementID = hoveredElement.closest(".playlist-event")?.id;

				if (elementID) {
					navigator.clipboard.writeText(elementID).then(() => {
						this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {message: "Event ID copied"});
					});
				}
			} else if (ev.key === "Escape") {
				// Handle escape button click while dragging or if cut/copied an event - cancel drag/cut/copy
				this.cancelDraggingOrCutOrCopyOnEscape(ev);
			}
		},

		handleMouseMovement(ev) {
			this.mousePosition.x = ev.clientX;
			this.mousePosition.y = ev.clientY;
		},

		async openEditExternalPlaylistEventDialog() {
			if (!this.currentOrSelectedShowEpisode.show) {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "An error occurred while trying to fetch the show."
				});
				return;
			}

			if (!this.currentOrSelectedShowEpisode.show.settings) {
				this.currentOrSelectedShowEpisode.show.settings = await this.$store
					.dispatch("getShowSettings", this.currentOrSelectedShowEpisode.object_id)
					.catch(() => {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "An error occurred when trying to fetch the show's recording duration."
						});
					});
			}
			this.editExternalPlaylistEventDialog = {
				open: true,
				options: {
					event: this.currentOrSelectedShowEpisode,
					show: this.currentOrSelectedShowEpisode.show,
					parent: this
				}
			};
		},

		async openShowAudioFeedsDialog() {
			if (!this.currentOrSelectedShowEpisode.show) {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "An error occurred while trying to fetch the show."
				});
				return;
			}

			if (!this.currentOrSelectedShowEpisode.show.settings) {
				this.currentOrSelectedShowEpisode.show.settings = await this.$store
					.dispatch("getShowSettings", this.currentOrSelectedShowEpisode.object_id)
					.catch(() => {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "An error occurred when trying to fetch the show's audio feeds."
						});
					});
			}
			this.showAudioFeedsDialog = {
				open: true,
				options: {
					show: this.currentOrSelectedShowEpisode.show
				}
			};
		},

		onMove(evt, originalEvent) {
			const draggedElement = document.querySelector(".dragged-playlist");
			this.onDragItem(evt, originalEvent, draggedElement);
			return false;
		},

		onDropHotkeyEvent(event) {
			this.onDropItem(event, true);
		},

		startShowPlaylist() {
			this.pendingStartEndShow = {pending: true, process: "start"};
			this.$store.dispatch("SequencerStartCustomPlaylist", {
				stationID: this.selectedStationID,
				PlaylistID: this.loadedPlaylists[0].PlaylistID
			});
		},

		endShowPlaylist() {
			this.pendingStartEndShow = {pending: true, process: "end"};
			this.$store.dispatch("SequencerStopCustomPlaylist", {
				stationID: this.selectedStationID,
				PlaylistID: this.loadedPlaylists[0].PlaylistID
			});
		},

		loadSpotsCurrentHour() {
			this.toolsMenuOpen = false;

			const from = this.selectedDateTime;
			const to = this.selectedDateTime.clone().add(1, "hour");

			const events = this.playlistsEvents.filter(
				e =>
					e.EventType === EventTypes.SpotBlock &&
					moment.tz(e.AirStartDateTime, this.stationTimezone).isBetween(from, to)
			);

			if (events) {
				this.loadSpots(events, from, to);
			}
		},

		loadSpotsCurrentDay() {
			this.toolsMenuOpen = false;

			const from = moment.tz(this.stationTimezone).startOf("day");
			const to = moment.tz(this.stationTimezone).endOf("day");

			const events = this.playlistsEvents.filter(
				e =>
					e.EventType === EventTypes.SpotBlock &&
					moment.tz(e.AirStartDateTime, this.stationTimezone).isBetween(from, to)
			);

			if (events) {
				this.loadSpots(events, from, to);
			}
		},

		loadSpotsCustomRange({from, to}) {
			this.toolsMenuOpen = false;

			const loadedPlaylistsInTimeRange = this.loadedPlaylists.filter(p =>
				moment.tz(p.ScheduledStartDateTime, this.stationTimezone).isBetween(from, to)
			);

			if (loadedPlaylistsInTimeRange.length > 0) {
				const events = this.playlistsEvents.filter(
					e =>
						e.EventType === EventTypes.SpotBlock &&
						moment.tz(e.AirStartDateTime, this.stationTimezone).isBetween(from, to)
				);

				if (events) {
					this.loadSpots(events, from, to);
				}
			} else {
				this.$store
					.dispatch("getPlaylistWithAssetsBySchedule", {
						stationID: this.selectedStationID,
						from,
						to,
						formatPlaylists: false
					})
					.then(async playlists => {
						playlists = await this.$store.dispatch("recalculatePlaylists", {playlists});

						let playlistEvents = playlists.map(p => p.Events).reduce((acc, curr) => acc.concat(curr), []);
						const events = playlistEvents.filter(
							e =>
								e.EventType === EventTypes.SpotBlock &&
								moment.tz(e.AirStartDateTime, this.stationTimezone).isBetween(from, to)
						);

						if (events) {
							this.loadSpots(events, from, to);
						}
					});
			}
		},

		loadSpots(events, from, to) {
			events.forEach(e => {
				const jobKey = uuid.v1();
				const payload = {
					OrganizationID: this.$store.getters.getOrgID(),
					StationID: this.selectedStationID,
					JobKey: jobKey,
					JobName: `LC_${this.selectedStationID}_${e.EventID}`,
					JobType: "LinearAdsLoad",
					JobRequestData: {
						StationID: this.selectedStationID,
						StationName: this.selectedStation.StationName,
						EventID: e.EventID,
						BlockEventID: e.EventID,
						LoadLocalSpotsByBlockIndex: true,
						Username: this.$store.getters.getUserFullName(),
						FromDateTime: from.toISOString(),
						ThroughDateTime: to.toISOString()
					}
				};
				this.$store.dispatch("runJob", payload).then(response => {
					this.$store.commit("addJob", {
						JobKey: response.JobKey,
						JobTime: response.CreatedDateTime,
						JobMessage: `Filling linear ad of spot block starting at: ${moment
							.tz(e.AirStartDateTime, this.stationTimezone)
							.format("HH:mm:ss")}`,
						StationID: this.selectedStationID,
						StationName: this.selectedStation.StationName
					});
				});
			});
		},

		resolveTagsCurrentHour(dontResolveAlreadyResolved) {
			this.toolsMenuOpen = false;

			const from = this.selectedDateTime;
			const to = this.selectedDateTime.clone().add(1, "hour");

			let unresolvedEvents = this.playlistsEvents.filter(
				p => p.ResolveTags?.length && moment.tz(p.AirStartDateTime, this.stationTimezone).isBetween(from, to)
			);

			if (unresolvedEvents) {
				this.resolveTag(unresolvedEvents, dontResolveAlreadyResolved);
			}
		},

		resolveTagsCurrentDay(dontResolveAlreadyResolved) {
			this.toolsMenuOpen = false;

			let unresolvedEvents = this.playlistsEvents.filter(
				p =>
					p.ResolveTags?.length &&
					moment
						.tz(p.AirStartDateTime, this.stationTimezone)
						.isBetween(
							moment.tz(this.stationTimezone).startOf("day"),
							moment.tz(this.stationTimezone).endOf("day")
						)
			);

			if (unresolvedEvents) {
				this.resolveTag(unresolvedEvents, dontResolveAlreadyResolved);
			}
		},

		resolveTagsCustomRange({from, to, dontResolveAlreadyResolved}) {
			this.toolsMenuOpen = false;

			let loadedPlaylistsInTimeRange = this.loadedPlaylists.filter(p =>
				moment.tz(p.ScheduledStartDateTime, this.stationTimezone).isBetween(from, to)
			);
			let unresolvedEvents;
			if (loadedPlaylistsInTimeRange.length > 0) {
				unresolvedEvents = this.playlistsEvents.filter(
					p =>
						p.ResolveTags?.length && moment.tz(p.AirStartDateTime, this.stationTimezone).isBetween(from, to)
				);
				if (unresolvedEvents) {
					this.resolveTag(unresolvedEvents, dontResolveAlreadyResolved);
				}
			} else {
				this.$store
					.dispatch("getPlaylistWithAssetsBySchedule", {
						stationID: this.selectedStationID,
						from,
						to,
						formatPlaylists: false
					})
					.then(async playlists => {
						playlists = await this.$store.dispatch("recalculatePlaylists", {playlists});

						let playlistEvents = playlists.reduce((acc, p) => acc.concat(p.Events), []);
						unresolvedEvents = playlistEvents.filter(
							p =>
								p.ResolveTags?.length &&
								moment.tz(p.AirStartDateTime, this.stationTimezone).isBetween(from, to)
						);
						if (unresolvedEvents) {
							this.resolveTag(unresolvedEvents, dontResolveAlreadyResolved);
						}
					});
			}
		},

		resolveTag(unresolvedEvents, dontResolveAlreadyResolved) {
			unresolvedEvents.forEach(event => {
				//Skip already resolved event
				if (dontResolveAlreadyResolved && event.Asset) {
					return;
				}
				this.$store.dispatch("getDefaultAssetTypes").then(response => {
					let assetTypes = "";
					if (response.status === 200 && response.data.LocalizerAssetTypeIDs) {
						assetTypes = response.data.LocalizerAssetTypeIDs.map(assetType => `"${assetType}"`);
					}
					this.$store
						.dispatch("resolveTag", {
							stationID: this.selectedStationID,
							eventID: event.EventID,
							playlistID: event.PlaylistID,
							searchShowDateTime: moment
								.tz(event.AirStartDateTime, this.stationTimezone)
								.toISOString(true),
							assetTypes: assetTypes
						})
						.then(response => {
							if (response.status === 200) {
								if (response.data.errors) {
									this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
										message: response.data.errors[0].message
									});
								} else {
									this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
										message: response.data.data.resolveVoiceTagEvent.resolveReason
									});
								}
							} else {
								this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
									message: "Could not resolve rotation tag!"
								});
							}
						});
				});
			});
		},

		togglePlaylistScrolling() {
			if (!this.$refs.scrollbar) {
				return;
			}
			// Check if there is a context menu element in the dom
			// If yes, disable the playlist scrolling by setting the scrollbar's handlers' array to []
			const disableScrolling = !!this.$refs.scrollbar.$el.querySelector(".context-menu-open");
			this.playlistScrollbarOptions.handlers = disableScrolling ? [] : defaultScrollbarHandlers;
		},

		loadPlaylistsDatePicker({year, month}) {
			const promises = [];
			const stationID = this.selectedStationID;
			const start = moment({year, month}).tz(this.stationTimezone).startOf("week");
			const end = moment({year, month}).endOf("month").tz(this.stationTimezone).endOf("week");

			while (start.isSameOrBefore(end)) {
				let from = start.clone();
				let to = start.clone().endOf("week");
				start.add(1, "week");

				const dateRange = `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;

				//If a request for this date range has already been initiated, continue with the next week.
				if (this.$store.getters.playlistsNoAssetsLoadedByDateRange(stationID, dateRange)) {
					continue;
				}

				//Set the key in the store as an identifier that a request for this date range has already been initiated.
				this.$store.commit("addLoadedPlaylistNoAssets", {stationID, dateRange});

				promises.push(
					this.$store
						.dispatch("getPlaylistBySchedule", {stationID, from, to, params: {ReturnChildEvents: false}})
						.then(response => {
							return {
								stationID,
								dateRange,
								playlists: response.data
							};
						})
						.catch(() => {
							return {
								stationID,
								dateRange,
								playlists: null
							};
						})
				);
			}

			if (promises.length > 0) {
				Promise.all(promises).then(results => {
					results.forEach(result => this.$store.commit("addLoadedPlaylistNoAssets", result));
				});
			}
		},

		updateMinWidth() {
			this.params?.api?.group?.api?.setConstraints({minimumWidth: PANEL_MIN_WIDTH});
		}
	}
};
</script>

<style lang="scss" scoped>
.playlist-event {
	position: unset;
}

:deep(.playlist-title-btn) {
	font-size: 14px;

	&:hover {
		> .v-btn__overlay {
			background: transparent;
		}

		> .v-btn__content {
			text-decoration: underline;
		}
	}
}
</style>
